import { isValidNarrationManifest, type NarrationManifest, type LocaleOption } from './course';

/** Serialized input remains permissive; the authoring loader normalizes block content. */
export interface LessonExportVariant {
  locale: string;
  lesson: {
    id?: string;
    lessonMeta: Record<string, unknown> & { id: string; locale: string };
    blocks: Record<string, Record<string, unknown> & { id: string; type: string }>;
    blockOrder: string[];
  };
  narration?: NarrationManifest;
}

export interface LessonExportBundle {
  kind: 'lesson-export-bundle';
  lessonId: string;
  accountId: number | null;
  defaultLocale: string;
  availableLocales: LocaleOption[];
  variants: LessonExportVariant[];
}

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const locale = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value === value.trim().toLowerCase();

export function isLessonExportBundle(value: unknown): value is LessonExportBundle {
  if (!record(value) || value.kind !== 'lesson-export-bundle'
    || typeof value.lessonId !== 'string' || !value.lessonId
    || !(value.accountId === null || (Number.isSafeInteger(value.accountId) && Number(value.accountId) > 0))
    || !locale(value.defaultLocale) || !Array.isArray(value.availableLocales)
    || !Array.isArray(value.variants)) return false;
  const locales: string[] = [];
  for (const entry of value.availableLocales) {
    if (!record(entry) || !locale(entry.locale) || typeof entry.label !== 'string') return false;
    locales.push(entry.locale);
  }
  if (locales[0] !== value.defaultLocale || new Set(locales).size !== locales.length
    || value.variants.length !== locales.length) return false;
  return value.variants.every((variant, index) => {
    if (!record(variant) || variant.locale !== locales[index] || !record(variant.lesson)) return false;
    const { lessonMeta, blocks, blockOrder, id } = variant.lesson;
    if ((id !== undefined && id !== value.lessonId) || !record(lessonMeta)
      || lessonMeta.id !== value.lessonId || lessonMeta.locale !== variant.locale
      || lessonMeta.defaultLocale !== value.defaultLocale
      || !record(blocks) || !Array.isArray(blockOrder)
      || !blockOrder.every((key): key is string => typeof key === 'string')
      || new Set(blockOrder).size !== blockOrder.length
      || Object.keys(blocks).length !== blockOrder.length) return false;
    if (!blockOrder.every((key) => record(blocks[key]) && blocks[key].id === key
      && typeof blocks[key].type === 'string')) return false;
    return variant.narration === undefined || isValidNarrationManifest(variant.narration);
  });
}
